import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { MagnifyingGlass, Plus, Archive, Paperclip, X, List as ListIcon, CaretUp, CaretDown, Bell, FileXls, PencilSimple, ArrowClockwise } from "@phosphor-icons/react";
import EinModal from "@/components/ein/EinModal";
import AddColumnModal from "@/components/shared/AddColumnModal";
import RestoreColumnsModal from "@/components/shared/RestoreColumnsModal";
import BulkUploadModal from "@/components/shared/BulkUploadModal";
import ReminderConfigModal from "@/components/shared/ReminderConfigModal";

const COLUMNS = [
  { id: "entity",                          label: "Entity" },
  { id: "ein",                             label: "EIN" },
  { id: "dos",                             label: "DOS" },
  { id: "creation_date",                   label: "Creation Date", type: "date" },
  { id: "entity_state",                    label: "Entity State" },
  { id: "manager",                         label: "Manager" },
  { id: "owned_real_estate_asset_1",       label: "Owned Real Estate Asset 1" },
  { id: "owned_real_estate_asset_2",       label: "Owned Real Estate Asset 2" },
  { id: "nys_biennial_last_renewal_date",  label: "NYS Biennial Last Renewal Date", type: "date" },
  { id: "biennial_expiration",             label: "Biennial Expiration",            type: "date" },
  { id: "nys_department_of_labor_er",      label: "NYS Department of Labor ER" },
  { id: "nybe_id",                         label: "NYBE ID" },
];

const fmtDate = (s) => {
  if (!s) return "";
  const d = new Date(String(s).length <= 10 ? String(s) + "T00:00:00" : String(s));
  if (Number.isNaN(d.getTime())) return "";
  return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}`;
};
const fmtLU = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d)) return "";
  return `${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}.${String(d.getFullYear()).slice(-2)}`;
};

function expiryTone(iso) {
  if (!iso) return null;
  const d = new Date(String(iso).length <= 10 ? String(iso) + "T00:00:00" : String(iso));
  if (Number.isNaN(d.getTime())) return null;
  const t = new Date(); t.setHours(0, 0, 0, 0);
  const diff = Math.round((d - t) / 86400000);
  if (diff < 0)  return { cls: "bg-rose-600 text-white",                                 rowCls: "bg-rose-100/70",   label: `${Math.abs(diff)}d overdue` };
  if (diff <=30) return { cls: "bg-rose-100 text-rose-700 border border-rose-200",       rowCls: "bg-rose-50/60",    label: `${diff}d` };
  if (diff <=60) return { cls: "bg-orange-100 text-orange-700 border border-orange-200", rowCls: "bg-orange-50/50",  label: `${diff}d` };
  if (diff <=90) return { cls: "bg-yellow-100 text-yellow-800 border border-yellow-200", rowCls: "bg-yellow-50/40",  label: `${diff}d` };
  return { cls: "text-slate-600", rowCls: "", label: `${diff}d` };
}

export default function EinPage() {
  const { user } = useAuth();
  const [access, setAccess] = useState(null);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [openEnt, setOpenEnt] = useState(null);
  const [newRow, setNewRow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showArchived, setShowArchived] = useState(true);
  const [showDeleted, setShowDeleted] = useState(false);
  // Hamburger + modals
  const [toolbarOpen, setToolbarOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [reminderCfgOpen, setReminderCfgOpen] = useState(false);
  const [addColOpen, setAddColOpen] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [reminderMode, setReminderMode] = useState(false);
  const [customCols, setCustomCols] = useState([]);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/ein/access"); setAccess(data); }
      catch { setAccess({ can_view: false }); }
    })();
  }, []);

  const load = useCallback(async () => {
    try {
      const [a, d] = await Promise.all([api.get("/ein"), api.get("/ein?deleted=true")]);
      setItems([...(a.data || []), ...(d.data || [])]);
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
  }, []);
  const loadCustomCols = useCallback(async () => {
    try { const { data } = await api.get("/ein/columns"); setCustomCols(Array.isArray(data) ? data : []); }
    catch { setCustomCols([]); }
  }, []);
  useEffect(() => { if (access?.can_view) { load(); loadCustomCols(); } }, [access?.can_view, load, loadCustomCols]);

  const active = useMemo(() => items.filter((x) => !x.archived && !x.deleted), [items]);
  const archived = useMemo(() => items.filter((x) => x.archived && !x.deleted), [items]);
  const deleted = useMemo(() => items.filter((x) => x.deleted), [items]);

  const filter = useCallback((list) => {
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((x) => JSON.stringify(x).toLowerCase().includes(q));
  }, [search]);

  const nextBiennial = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return active.filter((x) => x.biennial_expiration)
      .map((x) => ({ ...x, _d: new Date(x.biennial_expiration + "T00:00:00") }))
      .filter((x) => !Number.isNaN(x._d) && x._d >= today)
      .sort((a, b) => a._d - b._d)[0] || null;
  }, [active]);

  const startAdd = () => setNewRow(Object.fromEntries(COLUMNS.map((c) => [c.id, c.type === "date" ? null : ""])));

  const saveNew = async () => {
    if (!newRow) return;
    setSaving(true);
    try {
      const { data } = await api.post("/ein", newRow);
      toast.success(`Entity #${data.entity_seq} added`);
      setItems((prev) => [...prev, data]);
      setNewRow(null);
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
    finally { setSaving(false); }
  };

  const toggleArchive = async (x) => {
    try { const { data } = await api.patch(`/ein/${x.id}`, { field: "archived", value: !x.archived });
      setItems((prev) => prev.map((y) => y.id === x.id ? data : y));
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
  };
  const toggleReminderFlag = async (x) => {
    try { const { data } = await api.patch(`/ein/${x.id}`, { field: "email_reminder_enabled", value: !x.email_reminder_enabled });
      setItems((prev) => prev.map((y) => y.id === x.id ? data : y));
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
  };
  const softDelete = async (x) => {
    if (!window.confirm(`Move entity #${x.entity_seq} to Deleted?`)) return;
    try { await api.delete(`/ein/${x.id}`); setItems((p) => p.map((y) => y.id === x.id ? { ...y, deleted: true } : y)); }
    catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
  };
  const restoreDeleted = async (x) => {
    try { const { data } = await api.patch(`/ein/${x.id}`, { field: "deleted", value: false });
      setItems((p) => p.map((y) => y.id === x.id ? data : y));
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
  };
  const hardDelete = async (x) => {
    if (!window.confirm(`Purge entity #${x.entity_seq}?`)) return;
    try { await api.delete(`/ein/${x.id}?hard=true`); setItems((p) => p.filter((y) => y.id !== x.id)); }
    catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
  };

  if (access === null) return <div className="min-h-screen bg-[#F4F6F8]"><AppHeader /><div className="p-8 text-center text-sm text-slate-500">Loading…</div></div>;
  if (!access?.can_view) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen bg-[#F4F6F8]">
      <AppHeader />
      <main className="max-w-none mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 2xl:px-10 py-3" data-testid="ein-page">
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <h1 className="text-xl font-bold text-slate-900 mr-2" data-testid="ein-title">EIN</h1>
          <div className="relative flex-1 min-w-0 max-w-[520px]">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search entities…" className="pl-9 bg-white" data-testid="ein-search" />
          </div>
          <div className="ml-auto flex items-center gap-1.5 flex-wrap">
            <Button size="sm" onClick={startAdd} className="bg-[#1f64c2] hover:bg-[#1854a3]" data-testid="ein-add">
              <Plus size={13} className="mr-1" /> Add entity
            </Button>
            <Button size="sm" variant="outline" onClick={() => setToolbarOpen((s) => !s)} data-testid="ein-toolbar-toggle" title="More actions">
              <ListIcon size={14} className="mr-1" />
              {toolbarOpen ? <CaretUp size={11} /> : <CaretDown size={11} />}
            </Button>
          </div>
        </div>

        {toolbarOpen && (
          <div className="mb-3 rounded-lg border border-slate-200 bg-white p-2 flex flex-wrap gap-1.5" data-testid="ein-toolbar-panel">
            <Button size="sm" variant="outline" onClick={() => setBulkOpen(true)} data-testid="ein-bulk-open">
              <FileXls size={14} className="mr-1.5" /> Bulk upload
            </Button>
            <Button size="sm" variant="outline" onClick={startAdd} data-testid="ein-toggle-edit">
              <PencilSimple size={14} className="mr-1.5" /> Add / edit mode
            </Button>
            <Button size="sm" variant={reminderMode ? "default" : "outline"} onClick={() => setReminderMode((m) => !m)}
              data-testid="ein-reminder-mode"
              className={reminderMode ? "bg-amber-500 hover:bg-amber-600" : ""}>
              <Bell size={14} className="mr-1.5" /> {reminderMode ? "Exit reminder mode" : "Reminder flags"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setReminderCfgOpen(true)} data-testid="ein-email-reminders">
              <Bell size={14} className="mr-1.5" /> Email reminders
            </Button>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3" data-testid="ein-kpis">
          <div className="rounded-lg border border-slate-200 bg-white px-3 py-2" data-testid="kpi-count">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Total Entities</div>
            <div className="text-lg font-bold text-slate-900 leading-tight mt-0.5">{active.length}</div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white px-3 py-2" data-testid="kpi-next-biennial">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Next Biennial Expiration Date</div>
            <div className="text-lg font-bold text-slate-900 leading-tight mt-0.5">{nextBiennial ? fmtDate(nextBiennial.biennial_expiration) : "—"}</div>
            {nextBiennial && <div className="text-[10px] text-slate-500 mt-0.5 truncate">{nextBiennial.entity || "—"} | {nextBiennial.ein || "—"}</div>}
          </div>
          <div className="rounded-lg bg-white border border-slate-200 px-3 py-2 text-[10px]" data-testid="expiration-legend">
            <div className="uppercase tracking-widest text-slate-500 font-semibold mb-1">Expiration Legend</div>
            <div className="flex flex-wrap gap-1">
              <span className="bg-rose-100 text-rose-700 border border-rose-200 px-1.5 py-0.5 rounded-full font-semibold">≤30 d</span>
              <span className="bg-orange-100 text-orange-700 border border-orange-200 px-1.5 py-0.5 rounded-full font-semibold">≤60 d</span>
              <span className="bg-yellow-100 text-yellow-800 border border-yellow-200 px-1.5 py-0.5 rounded-full font-semibold">≤90 d</span>
              <span className="bg-rose-600 text-white px-1.5 py-0.5 rounded-full font-semibold">Expired</span>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
          <SectionHeader label={`Active entities (${filter(active).length}${search ? ` of ${active.length}` : ""})`} />
          <div className="flex items-center gap-1.5">
            <Button size="sm" variant="outline" onClick={() => setAddColOpen(true)} className="text-[11px] h-7 px-2 border-slate-300 text-slate-700 hover:bg-slate-100" data-testid="ein-add-column" title="Add a custom column">
              <Plus size={11} className="mr-1" /> Add column
            </Button>
            <Button size="sm" variant="outline" onClick={() => setRestoreOpen(true)} className="text-[11px] h-7 px-2 border-slate-300 text-slate-700 hover:bg-slate-100" data-testid="ein-restore-columns" title="Restore a previously-deleted column">
              <ArrowClockwise size={11} className="mr-1" /> Restore custom columns
            </Button>
          </div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-xs" data-testid="ein-table-active">
            <thead>
              <tr className="bg-slate-50">
                {reminderMode && <th className="text-[10px] uppercase tracking-widest font-bold text-slate-500 px-2 py-1.5 border-b border-slate-200 text-center">Flag</th>}
                <th className="text-[10px] uppercase tracking-widest font-bold text-slate-500 px-2 py-1.5 border-b border-slate-200">#</th>
                {COLUMNS.slice(0, 5).map((c) => (
                  <th key={c.id} className="text-[10px] uppercase tracking-widest font-bold text-slate-500 px-2 py-1.5 border-b border-slate-200 whitespace-nowrap">{c.label}</th>
                ))}
                <th className="text-[10px] uppercase tracking-widest font-bold text-slate-500 px-2 py-1.5 border-b border-slate-200 text-center">Attach.</th>
                {COLUMNS.slice(5).map((c) => (
                  <th key={c.id} className="text-[10px] uppercase tracking-widest font-bold text-slate-500 px-2 py-1.5 border-b border-slate-200 whitespace-nowrap">{c.label}</th>
                ))}
                {customCols.map((c) => (
                  <th key={c.id} className="text-[10px] uppercase tracking-widest font-bold text-slate-500 px-2 py-1.5 border-b border-slate-200 whitespace-nowrap">{c.label}</th>
                ))}
                <th className="text-[10px] uppercase tracking-widest font-bold text-slate-500 px-2 py-1.5 border-b border-slate-200">Actions</th>
              </tr>
            </thead>
            <tbody>
              {newRow && (
                <tr className="bg-[#eaf2ff]/40" data-testid="ein-new-row">
                  {reminderMode && <td></td>}
                  <td className="px-2 py-0.5 border-b border-slate-100 text-[11px] font-bold text-slate-500">NEW</td>
                  {COLUMNS.slice(0, 5).map((c) => (
                    <td key={c.id} className="px-1.5 py-0.5 border-b border-slate-100 min-w-[110px]">
                      <input type={c.type || "text"} value={newRow[c.id] ?? ""} onChange={(e) => setNewRow({ ...newRow, [c.id]: e.target.value || null })} className="w-full px-1 py-0.5 border border-slate-300 rounded text-xs" data-testid={`ein-new-${c.id}`} />
                    </td>
                  ))}
                  <td></td>
                  {COLUMNS.slice(5).map((c) => (
                    <td key={c.id} className="px-1.5 py-0.5 border-b border-slate-100 min-w-[110px]">
                      <input type={c.type || "text"} value={newRow[c.id] ?? ""} onChange={(e) => setNewRow({ ...newRow, [c.id]: e.target.value || null })} className="w-full px-1 py-0.5 border border-slate-300 rounded text-xs" data-testid={`ein-new-${c.id}`} />
                    </td>
                  ))}
                  {customCols.map((c) => <td key={c.id}></td>)}
                  <td className="px-2 py-0.5 border-b border-slate-100">
                    <Button size="sm" onClick={saveNew} disabled={saving} className="h-6 px-2 text-[10px] bg-[#327de3] hover:bg-[#1f64c2]" data-testid="ein-new-save">Save</Button>
                    <Button size="sm" variant="ghost" onClick={() => setNewRow(null)} className="h-6 px-2 text-[10px]"><X size={11} /></Button>
                  </td>
                </tr>
              )}
              {filter(active).map((x) => <EinRow key={x.id} x={x} customCols={customCols} reminderMode={reminderMode} onToggleReminder={() => toggleReminderFlag(x)} onOpen={() => setOpenEnt(x)} onArchive={() => toggleArchive(x)} />)}
              {filter(active).length === 0 && !newRow && (
                <tr><td colSpan={COLUMNS.length + customCols.length + 3 + (reminderMode ? 1 : 0)} className="text-center text-slate-400 italic py-6 text-xs">{search ? "No entities match." : "No entities yet."}</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-4">
          <button onClick={() => setShowArchived((s) => !s)} className="text-[11px] uppercase tracking-widest font-bold text-slate-500 inline-flex items-center gap-1" data-testid="ein-archived-toggle">
            <span className={`inline-block transition-transform ${showArchived ? "rotate-90" : ""}`}>▸</span>
            Archived entities ({filter(archived).length})
          </button>
          {showArchived && (
            <div className="mt-1 rounded-lg border border-slate-200 bg-slate-50/60 overflow-x-auto">
              <table className="w-full text-xs" data-testid="ein-table-archived">
                <tbody>
                  {filter(archived).map((x) => <EinRow key={x.id} x={x} customCols={customCols} dim onOpen={() => setOpenEnt(x)} onArchive={() => toggleArchive(x)} onDelete={() => softDelete(x)} />)}
                  {filter(archived).length === 0 && <tr><td className="text-center text-slate-400 italic py-4 text-xs">No archived entities.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-3">
          <button onClick={() => setShowDeleted((s) => !s)} className="text-[10px] uppercase tracking-widest font-semibold text-slate-400 inline-flex items-center gap-1" data-testid="ein-deleted-toggle">
            <span className={`inline-block transition-transform ${showDeleted ? "rotate-90" : ""}`}>▸</span>
            Deleted entities ({filter(deleted).length})
          </button>
          {showDeleted && (
            <div className="mt-1 rounded-md border border-slate-200 bg-slate-50/40 overflow-x-auto">
              <table className="w-full text-[10px]">
                <tbody>
                  {filter(deleted).map((x) => (
                    <tr key={x.id} className="opacity-60">
                      <td className="px-1.5 py-0.5 border-b border-slate-100 font-semibold text-slate-500">#{x.entity_seq}</td>
                      <td className="px-1.5 py-0.5 border-b border-slate-100">{x.entity}</td>
                      <td className="px-1.5 py-0.5 border-b border-slate-100">
                        <button onClick={() => restoreDeleted(x)} className="text-[9px] font-semibold text-emerald-700 hover:underline mr-1">Restore</button>
                        {user?.is_super_admin && <button onClick={() => hardDelete(x)} className="text-[9px] font-semibold text-rose-600 hover:underline">Purge</button>}
                      </td>
                    </tr>
                  ))}
                  {filter(deleted).length === 0 && <tr><td className="text-center text-slate-400 italic py-2 text-[10px]">No deleted entities.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      <EinModal entity={openEnt} customCols={customCols}
        onClose={() => { setOpenEnt(null); load(); }}
        onChange={(u) => { setItems((prev) => prev.map((y) => y.id === u.id ? u : y)); setOpenEnt(u); }}
      />

      <AddColumnModal
        open={addColOpen}
        onClose={() => setAddColOpen(false)}
        onCreated={() => loadCustomCols()}
        basePath="/ein"
        defaultLabels={COLUMNS.map((c) => c.label).concat(["#", "Attach."])}
        testIdPrefix="ein-add-column"
      />
      <RestoreColumnsModal
        open={restoreOpen}
        onClose={() => setRestoreOpen(false)}
        onRestored={() => loadCustomCols()}
        basePath="/ein"
        testIdPrefix="ein-restore-columns"
      />
      <BulkUploadModal
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onImported={() => load()}
        basePath="/ein"
        entityLabel="entities"
        templateFilename="STRATA EIN TEMPLATE.xlsx"
        templateCols={[
          { key: "entity", label: "Entity", sample: "1 Carnation LLC" },
          { key: "ein", label: "EIN", sample: "12-3456789" },
          { key: "dos", label: "DOS" },
          { key: "creation_date", label: "Creation Date", date: true, sample: "2020-01-15" },
          { key: "entity_state", label: "Entity State", sample: "NY" },
          { key: "manager", label: "Manager" },
          { key: "owned_real_estate_asset_1", label: "Owned Real Estate Asset 1" },
          { key: "owned_real_estate_asset_2", label: "Owned Real Estate Asset 2" },
          { key: "nys_biennial_last_renewal_date", label: "NYS Biennial Last Renewal Date", date: true },
          { key: "biennial_expiration", label: "Biennial Expiration", date: true, sample: "2027-01-15" },
          { key: "nys_department_of_labor_er", label: "NYS Department of Labor ER" },
          { key: "nybe_id", label: "NYBE ID" },
        ]}
        testIdPrefix="ein-bulk"
      />
      <ReminderConfigModal
        open={reminderCfgOpen}
        onClose={() => setReminderCfgOpen(false)}
        isSuperAdmin={!!user?.is_super_admin}
        basePath="/ein"
        entityLabel="entities"
        entityCountField="flagged_entities"
        testIdPrefix="ein-reminder-config"
      />
    </div>
  );
}

function SectionHeader({ label }) {
  return <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-1.5 mt-1">{label}</div>;
}

function EinRow({ x, customCols = [], reminderMode, onToggleReminder, dim, onOpen, onArchive, onDelete }) {
  const tone = expiryTone(x.biennial_expiration);
  const rowCls = !x.archived && tone ? tone.rowCls : "";
  const cf = x.custom_fields || {};
  return (
    <tr className={`hover:bg-[#eaf2ff]/40 cursor-pointer border-b border-slate-100 ${dim ? "opacity-70" : ""} ${x.archived ? "bg-emerald-50/70" : rowCls}`}
        onClick={onOpen} data-testid={`ein-row-${x.id}`}>
      {reminderMode && (
        <td className="px-2 py-0.5 text-center" onClick={(e) => e.stopPropagation()}>
          <input type="checkbox" checked={!!x.email_reminder_enabled} onChange={onToggleReminder}
            className="w-3.5 h-3.5 accent-amber-500" data-testid={`ein-reminder-${x.id}`} />
        </td>
      )}
      <td className="px-2 py-0.5 font-bold text-slate-700 whitespace-nowrap">#{x.entity_seq}</td>
      {COLUMNS.slice(0, 5).map((c) => (
        <td key={c.id} className="px-2 py-0.5 whitespace-nowrap">
          {c.type === "date" ? fmtDate(x[c.id]) : (x[c.id] || "")}
        </td>
      ))}
      <td className="px-2 py-0.5 whitespace-nowrap text-center">
        {x.has_attachments ? <Paperclip size={13} weight="bold" className="inline text-slate-600" /> : <span className="inline-block w-5 h-3 bg-rose-200 border border-rose-300 rounded-sm" />}
      </td>
      {COLUMNS.slice(5).map((c) => {
        let content;
        if (c.id === "biennial_expiration") {
          const t = expiryTone(x.biennial_expiration);
          content = <div className="flex items-center gap-1.5"><span>{fmtDate(x.biennial_expiration)}</span>{t && <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full ${t.cls}`}>{t.label}</span>}</div>;
        } else content = c.type === "date" ? fmtDate(x[c.id]) : (x[c.id] || "");
        return <td key={c.id} className="px-2 py-0.5 whitespace-nowrap">{content}</td>;
      })}
      {customCols.map((cc) => (
        <td key={cc.id} className="px-2 py-0.5 whitespace-nowrap text-slate-700">{cf[cc.key] ?? ""}</td>
      ))}
      <td className="px-2 py-0.5 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-1">
          <button onClick={onArchive}
            className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${x.archived ? "bg-white border-emerald-300 text-emerald-700 hover:bg-emerald-100" : "bg-white border-slate-200 text-slate-500 hover:border-rose-300 hover:text-rose-600"}`}
            data-testid={`ein-archive-${x.id}`}>
            <Archive size={10} className="inline mr-0.5" />{x.archived ? "Restore" : "Archive"}
          </button>
          {x.archived && onDelete && (
            <button onClick={onDelete} className="text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-white border-slate-200 text-slate-500 hover:border-rose-400 hover:text-rose-600"
              data-testid={`ein-delete-${x.id}`}>Delete</button>
          )}
        </div>
      </td>
    </tr>
  );
}
